// Базовые элементы интерфейса по Orbis Design System: кнопки, бейджи, вкладки, переключатели, состояния.
import { useId, type ButtonHTMLAttributes, type ReactNode } from 'react'
import type { Tone } from '../types.ts'
import { Icon, type IconName } from './Icon.tsx'

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

/** primary — главное действие; secondary — вторичная; quiet — тихая; ghost — без рамки; danger — опасная (явное подтверждение);
 *  danger-quiet — опасная, но спокойная (в строке таблицы). */
type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'ghost' | 'danger' | 'danger-quiet'

const BTN_BASE =
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-ctl border font-semibold transition-[background-color,border-color,filter] select-none disabled:opacity-50 disabled:pointer-events-none'
const BTN_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-accent text-accent-fg hover:brightness-[1.06]',
  secondary: 'border-line-strong bg-surface text-fg-2 hover:bg-surface-2',
  quiet: 'border-transparent bg-surface-2 text-fg-2 hover:bg-surface-3',
  ghost: 'border-transparent bg-transparent text-muted hover:bg-surface-2 hover:text-fg',
  danger: 'border-transparent bg-danger text-accent-fg hover:brightness-[1.06]',
  'danger-quiet': 'border-line-strong bg-surface text-danger hover:bg-danger-soft hover:border-transparent',
}
const BTN_SIZES = {
  xs: 'h-6 px-2 text-[12px]',
  sm: 'h-7 px-2.5 text-[12.5px]',
  md: 'h-8 px-3.5 text-[13.5px]',
} as const

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: keyof typeof BTN_SIZES
  icon?: IconName
  loading?: boolean
}) {
  return (
    <button
      type={type}
      className={cx(BTN_BASE, BTN_VARIANTS[variant], BTN_SIZES[size], className)}
      disabled={rest.disabled || loading}
      {...rest}
    >
      {loading ? <Spinner size={14} /> : icon ? <Icon name={icon} size={size === 'xs' ? 13 : 15} /> : null}
      {children}
    </button>
  )
}

export function IconButton({
  icon,
  label,
  size = 'md',
  className,
  active,
  type = 'button',
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  icon: IconName
  label: string
  size?: 'sm' | 'md'
  active?: boolean
}) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-ctl text-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-40',
        size === 'sm' ? 'h-6 w-6' : 'h-8 w-8',
        active && 'bg-surface-2 text-fg',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
    </button>
  )
}

/** Светофор: цвет — только для смысла (норма / внимание / сбой). Акцент бренда — редко. */
export const TONE_BADGE: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-muted border-line',
  accent: 'bg-accent-soft text-accent border-transparent',
  ok: 'bg-ok-soft text-ok border-transparent',
  warn: 'bg-warn-soft text-warn border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
}

export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-muted',
  accent: 'text-accent',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
}

export const TONE_BG: Record<Tone, string> = {
  neutral: 'bg-subtle',
  accent: 'bg-accent',
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
}

export function Badge({
  tone = 'neutral',
  children,
  className,
  title,
  dot,
}: {
  tone?: Tone
  children: ReactNode
  className?: string
  title?: string
  /** Маленькая точка цвета статуса перед текстом (как .oui-badge .oui-d). */
  dot?: boolean
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-full border px-2 text-[11.5px] leading-none font-semibold whitespace-nowrap',
        TONE_BADGE[tone],
        className,
      )}
    >
      {dot ? <span aria-hidden="true" className={cx('h-1.5 w-1.5 rounded-full', TONE_BG[tone])} /> : null}
      {children}
    </span>
  )
}

export function Spinner({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <span className={cx('inline-flex animate-spin', className)} aria-hidden="true">
      <Icon name="loader" size={size} />
    </span>
  )
}

/** Скелетон вместо крутилки: пара полос, пока данные едут. */
export function Loading({ label = 'Загрузка…', rows = 3 }: { label?: string; rows?: number }) {
  const widths = ['62%', '84%', '46%', '73%', '55%']
  return (
    <div className="flex flex-col gap-2.5 py-6" role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="sk" style={{ width: widths[i % widths.length] }} />
      ))}
    </div>
  )
}

export function Empty({ children, icon }: { children: ReactNode; icon?: IconName }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-card border border-dashed border-line-strong px-4 py-6 text-center text-[13px] text-muted">
      {icon ? <Icon name={icon} size={18} className="text-subtle" /> : null}
      <div className="max-w-full [overflow-wrap:anywhere]">{children}</div>
    </div>
  )
}

export function ErrorBox({ error, onRetry }: { error: Error | string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-card bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
      <Icon name="alertCircle" className="mt-0.5 shrink-0" />
      {/* коды ошибок, адреса и пути — без пробелов: переносим где угодно, а не за край */}
      <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">{typeof error === 'string' ? error : error.message}</div>
      {onRetry ? (
        <Button size="xs" onClick={onRetry} icon="refresh">
          Повторить
        </Button>
      ) : null}
    </div>
  )
}

export interface TabItem<T extends string> {
  id: T
  label: ReactNode
  count?: number
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className,
  label,
}: {
  items: TabItem<T>[]
  value: T
  onChange: (v: T) => void
  className?: string
  label: string
}) {
  return (
    // overflow-y-hidden: подчёркивание вкладки заходит на рамку (-mb-px), без него у полосы появлялась лишняя вертикальная прокрутка
    <div role="tablist" aria-label={label} className={cx('scroll-thin flex min-w-0 gap-0.5 overflow-x-auto overflow-y-hidden border-b border-line', className)}>
      {items.map((t) => {
        const active = t.id === value
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
              const idx = items.findIndex((x) => x.id === value)
              const next = items[(idx + (e.key === 'ArrowRight' ? 1 : items.length - 1)) % items.length]
              if (next) onChange(next.id)
            }}
            className={cx(
              '-mb-px flex h-9 shrink-0 items-center gap-1.5 border-b-2 px-3 text-[13px] font-medium transition-colors',
              active ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg',
            )}
          >
            {t.label}
            {t.count !== undefined && t.count > 0 ? (
              <span className="tnum rounded-full bg-surface-2 px-1.5 text-[11px] leading-[17px] text-muted">{t.count}</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

export function Segmented<T extends string>({
  items,
  value,
  onChange,
  label,
}: {
  items: { id: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div role="radiogroup" aria-label={label} className="scroll-thin inline-flex max-w-full rounded-[8px] border border-line bg-surface-2 p-0.5 max-md:overflow-x-auto">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          role="radio"
          aria-checked={it.id === value}
          onClick={() => onChange(it.id)}
          className={cx(
            // рамка фокуса внутри кнопки: на телефоне полоса прокручивается и обрезала бы рамку снаружи
            'inline-flex h-7 shrink-0 items-center gap-1 rounded-ctl px-2.5 text-[12.5px] font-medium whitespace-nowrap transition-colors focus-visible:outline-offset-[-2px]',
            it.id === value ? 'bg-surface text-fg shadow-card' : 'text-muted hover:text-fg',
          )}
        >
          {it.label}
        </button>
      ))}
    </div>
  )
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
  description,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
  disabled?: boolean
  description?: ReactNode
}) {
  const id = useId()
  return (
    <div className="flex items-start gap-3">
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors disabled:opacity-50',
          checked ? 'border-transparent bg-accent' : 'border-line-strong bg-surface-3',
        )}
      >
        <span
          className={cx(
            'inline-block h-4 w-4 rounded-full bg-surface shadow-card transition-transform',
            checked ? 'translate-x-[17px]' : 'translate-x-[1px]',
          )}
        />
      </button>
      <label htmlFor={id} className="min-w-0 cursor-pointer">
        <div className="text-[13.5px] font-medium [overflow-wrap:anywhere]">{label}</div>
        {description ? <div className="text-[12.5px] text-muted [overflow-wrap:anywhere]">{description}</div> : null}
      </label>
    </div>
  )
}

export function Field({
  label,
  hint,
  children,
  className,
  htmlFor,
}: {
  label: ReactNode
  hint?: ReactNode
  children: ReactNode
  className?: string
  htmlFor?: string
}) {
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="lbl">
        {label}
      </label>
      {children}
      {hint ? <div className="text-[12px] text-muted">{hint}</div> : null}
    </div>
  )
}

/** Карточка ERP: подпись капителью (.ct) + действия справа. */
export function Section({
  title,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) {
  return (
    <section className={cx('panel', className)}>
      {title || actions ? (
        // на узком экране действия переносятся под подпись, а не уезжают за край карточки
        <div className="flex min-h-[46px] flex-wrap items-center justify-between gap-2 border-b border-line px-[18px] py-2.5 max-md:px-3.5">
          <h2 className="ct min-w-0 [overflow-wrap:anywhere]">{title}</h2>
          {actions ? <div className="flex max-w-full flex-wrap items-center gap-1.5">{actions}</div> : null}
        </div>
      ) : null}
      <div className={cx('min-w-0 px-[18px] py-4 max-md:px-3.5', bodyClassName)}>{children}</div>
    </section>
  )
}

/** Заголовок страницы по образцу ERP: крошка, h1, пояснение, действия справа. */
export function PageHeader({
  title,
  lede,
  crumb,
  actions,
  className,
}: {
  title: ReactNode
  lede?: ReactNode
  crumb?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <div className={cx('mb-5 flex flex-wrap items-end justify-between gap-x-4 gap-y-3', className)}>
      <div className="min-w-0 max-w-full">
        {crumb ? <div className="crumb">{crumb}</div> : null}
        <h1 className="h1">{title}</h1>
        {lede ? <p className="lede">{lede}</p> : null}
      </div>
      {actions ? <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}

/** «Работает» — спокойная зелёная точка; «в очереди» — полая серая. */
export function LiveDot({ className, label }: { className?: string; label?: string }) {
  return (
    <span
      className={cx('live-dot', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      title={label}
      aria-hidden={label ? undefined : true}
    />
  )
}

export function QueuedDot({ className, label }: { className?: string; label?: string }) {
  return (
    <span
      className={cx('queued-dot', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      title={label}
      aria-hidden={label ? undefined : true}
    />
  )
}

export function Dot({ tone = 'neutral', className }: { tone?: Tone; className?: string }) {
  return <span aria-hidden="true" className={cx('inline-block h-2 w-2 shrink-0 rounded-full', TONE_BG[tone], className)} />
}

/** Плитка показателя (как .metric в ERP). */
export function Stat({ label, value, tone, hint }: { label: string; value: ReactNode; tone?: Tone; hint?: ReactNode }) {
  return (
    <div
      className={cx(
        'min-w-0 rounded-[9px] border border-line bg-surface-2 px-3 py-2.5',
        tone === 'warn' && 'shadow-[inset_3px_0_0_var(--orbis-warn)]',
        tone === 'danger' && 'shadow-[inset_3px_0_0_var(--orbis-bad)]',
        tone === 'ok' && 'shadow-[inset_3px_0_0_var(--orbis-good)]',
      )}
    >
      <div className={cx('tnum text-[20px] leading-tight font-[680] tracking-[-0.02em]', tone && tone !== 'neutral' ? TONE_TEXT[tone] : 'text-fg')}>
        {value}
      </div>
      <div className="mt-1.5 text-[11.5px] leading-tight text-muted [overflow-wrap:anywhere]">{label}</div>
      {hint ? <div className="mt-0.5 text-[11px] text-subtle [overflow-wrap:anywhere]">{hint}</div> : null}
    </div>
  )
}

export function Collapsible({
  summary,
  children,
  defaultOpen,
  className,
}: {
  summary: ReactNode
  children: ReactNode
  defaultOpen?: boolean
  className?: string
}) {
  return (
    <details className={cx('group', className)} open={defaultOpen}>
      <summary className="flex min-w-0 cursor-pointer list-none items-center gap-1 text-[12.5px] text-muted select-none hover:text-fg [&::-webkit-details-marker]:hidden">
        <Icon name="chevronRight" size={14} className="shrink-0 transition-transform group-open:rotate-90" />
        {summary}
      </summary>
      <div className="mt-1.5 min-w-0">{children}</div>
    </details>
  )
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>
}
